import React, { useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { Add, Download, Upload } from '@mui/icons-material';

interface Tournament {
  name: string;
  date: string;
  id: string;
}

interface TournamentsViewProps {
  tournaments: Tournament[];
  currentTournament: Tournament | null;
  onCreateTournament: () => void;
  onSelectTournament: (tournament: Tournament) => void;
  onExportTournament?: () => void;
  onImportTournament?: (file: File) => Promise<void>;
  exportLoading?: boolean;
  importLoading?: boolean;
  exportError?: string | null;
  importError?: string | null;
  exportSuccess?: string | null;
  importSuccess?: string | null;
}

const TournamentsView: React.FC<TournamentsViewProps> = ({
  tournaments,
  currentTournament,
  onCreateTournament,
  onSelectTournament,
  onExportTournament,
  onImportTournament,
  exportLoading = false,
  importLoading = false,
  exportError,
  importError,
  exportSuccess,
  importSuccess,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const handleExportClick = () => {
    if (onExportTournament) {
      onExportTournament();
    }
  };

  const handleImportClick = () => {
    setImportDialogOpen(true);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onImportTournament) {
      await onImportTournament(file);
      setImportDialogOpen(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  return (
    <Box sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Tournaments
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onExportTournament && (
            <Button
              variant="outlined"
              onClick={handleExportClick}
              disabled={exportLoading || !currentTournament}
              startIcon={<Download />}
            >
              {exportLoading ? 'Exporting...' : 'Export'}
            </Button>
          )}
          {onImportTournament && (
            <Button
              variant="outlined"
              onClick={handleImportClick}
              disabled={importLoading}
              startIcon={<Upload />}
            >
              {importLoading ? 'Importing...' : 'Import'}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onCreateTournament}
            startIcon={<Add />}
          >
            Create Tournament
          </Button>
        </Box>
      </Box>

      {/* Alerts for export/import feedback */}
      {(exportError || importError || exportSuccess || importSuccess) && (
        <Box sx={{ mb: 3 }}>
          {exportError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {exportError}
            </Alert>
          )}
          {importError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {importError}
            </Alert>
          )}
          {exportSuccess && (
            <Alert severity="success" sx={{ mb: 1 }}>
              {exportSuccess}
            </Alert>
          )}
          {importSuccess && (
            <Alert severity="success" sx={{ mb: 1 }}>
              {importSuccess}
            </Alert>
          )}
        </Box>
      )}
      
      {tournaments.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No tournaments yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first tournament to get started
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onCreateTournament}
            startIcon={<Add />}
          >
            Create New Tournament
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {tournaments.map((tournament) => (
            <Grid item xs={12} md={6} lg={4} key={tournament.id}>
              <Paper 
                sx={{ 
                  p: 3, 
                  cursor: 'pointer',
                  border: currentTournament?.id === tournament.id ? 2 : 1,
                  borderColor: currentTournament?.id === tournament.id ? 'primary.main' : 'grey.300',
                  '&:hover': {
                    boxShadow: 3,
                    borderColor: 'primary.main'
                  }
                }}
                onClick={() => onSelectTournament(tournament)}
              >
                <Typography variant="h6" gutterBottom>
                  {tournament.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {tournament.date}
                </Typography>
                {currentTournament?.id === tournament.id && (
                  <Chip 
                    label="Current" 
                    color="primary" 
                    size="small" 
                    sx={{ mb: 1 }}
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  Click to {currentTournament?.id === tournament.id ? 'manage' : 'switch to'} tournament
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)}>
        <DialogTitle>Import Tournament</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a tournament export file to import. This will add the tournament and all its wrestlers to your current tournaments.
          </Typography>
          <TextField
            type="file"
            inputRef={fileInputRef}
            onChange={handleFileSelect}
            inputProps={{
              accept: '.json',
            }}
            fullWidth
            helperText="Select a .json tournament export file"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TournamentsView;
